import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { useQuery } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { OperationEventClient, type ProductionEventKind, type ProductionOperationEventDTO } from "../../../api/floor";
import { formatNumber } from "../../../api/manufacturing";
import { when } from "./lotModel";
import { OverrideMark } from "./StepDialog";

export function eventKindLabel(t: TFunction, kind: ProductionEventKind): string {
  return {
    START: t("lot.event.kind.start", "Started"),
    REPORT: t("lot.event.kind.report", "Reported"),
    SEND: t("lot.event.kind.send", "Sent out"),
    MISSING: t("lot.event.kind.missing", "Missing"),
    FINISH: t("lot.event.kind.finish", "Finished"),
    SKIP: t("lot.event.kind.skip", "Skipped"),
    REOPEN: t("lot.event.kind.reopen", "Reopened"),
  }[kind];
}

/**
 * Everything reported on the lot's floor, newest first: the event log the
 * steps' figures are summed from. Read-only; a mistake is corrected by another
 * report, which shows here too. A report past what the step allowed shows its
 * override: by how much and why.
 */
export function HistoryTab({ lotId }: { lotId: number }) {
  const { t, i18n } = useTranslation();
  const { http } = useAuth();
  const events = useQuery({
    queryKey: ["production", "operation-event", "lot", lotId],
    queryFn: () => new OperationEventClient(http).forLot(lotId, 1000),
  });

  const cols = useMemo(() => {
    const c = defineColumns<ProductionOperationEventDTO>();
    return [
      c.text("recorded_at", (row) => when(row.recorded_at, i18n.language), { headerKey: "lot.history.when", priority: 1 }),
      c.text("process_code", (row) => row.process_name ?? row.process_code ?? "", { headerKey: "lot.history.process", priority: 1 }),
      c.text("component_code", (row) => row.component_code ?? t("lot.lot-level", "Whole lot"), { headerKey: "lot.history.component", priority: 1 }),
      c.text("part_number", (row) => row.part_number ?? "", { headerKey: "column.part.number", priority: 3 }),
      c.text("kind", (row) => eventKindLabel(t, row.kind), { headerKey: "lot.history.kind", priority: 1 }),
      c.text("quantity_good", (row) => (Number(row.quantity_good) === 0 ? "" : formatNumber(row.quantity_good)), {
        headerKey: "lot.step.good",
        priority: 2,
      }),
      c.text("quantity_scrap", (row) => (Number(row.quantity_scrap) === 0 ? "" : formatNumber(row.quantity_scrap)), {
        headerKey: "lot.step.scrap",
        priority: 2,
      }),
      c.text("scrap_reason_name", (row) => row.scrap_reason_name ?? "", { headerKey: "lot.step.reason", priority: 4 }),
      c.text("quantity_sent", (row) => (Number(row.quantity_sent ?? 0) === 0 ? "" : formatNumber(row.quantity_sent)), {
        headerKey: "lot.step.sent",
        priority: 3,
      }),
      c.custom(
        "is_override",
        (row) => (row.is_override ? `${String(row.quantity_over ?? "")} ${row.override_reason ?? ""}` : ""),
        (row) => (row.is_override ? <OverrideMark event={row} /> : null),
        { headerKey: "lot.history.override", priority: 2 },
      ),
      c.text("quantity_missing", (row) => (row.kind === "MISSING" ? formatNumber(row.quantity_missing ?? 0) : ""), {
        headerKey: "lot.step.missing",
        priority: 3,
      }),
      c.text("station_code", (row) => row.station_code ?? "", { headerKey: "lot.history.station", priority: 2 }),
      c.text("operator_name", (row) => row.operator_name ?? "", { headerKey: "lot.history.operator", priority: 3 }),
      c.text("pallet_number", (row) => (row.pallet_number == null ? "" : String(row.pallet_number)), {
        headerKey: "lot.history.pallet",
        defaultVisible: false,
      }),
      c.text("note", (row) => row.note ?? "", { headerKey: "column.note", priority: 4 }),
    ];
  }, [t, i18n.language]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DataTable<ProductionOperationEventDTO>
        data={events.data ?? []}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={events.isLoading}
        emptyMessage={t("lot.history.empty", "Nothing reported on the floor yet.")}
      />
    </div>
  );
}
